/**
 * CrossChannelMemoryPanel - Phase H7.
 *
 * Read-only view of `GET /api/contacts/:id/memory`. Renders the cross-
 * channel memory bundle the worker assembles in
 * `apps/worker/src/modules/conversations/conversation-memory.service.ts`.
 *
 * No editing in H7. Empty states render as small "-" placeholders so the
 * panel stays compact for fresh contacts.
 *
 * No handoff tokens are surfaced here - token introspection lives on a
 * separate (token-keyed) endpoint that returns a MASKED token.
 */

import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChannelBadge } from "@/components/badges/ChannelBadge";
import { formatRelative, formatAbsolute } from "@/lib/format";
import { Loader2 } from "lucide-react";
import { getContactMemory } from "@/lib/api-handoffs";
import type { CrossChannelContext } from "@/lib/api-handoffs";

export function CrossChannelMemoryPanel({ contactId }: { contactId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["contact-memory", contactId],
    queryFn: () => getContactMemory(contactId),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cross-channel memory</CardTitle>
        </CardHeader>
        <CardContent className="py-6">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cross-channel memory</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-400">
            Memory bundle unavailable.{" "}
            {error instanceof Error ? error.message : "Unknown error."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Cross-channel memory</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm">
          <LinkedIdentities identities={data.identities} />
          <RelatedConversations related={data.relatedConversations} />
          <LastKnownProperty value={data.lastKnownProperty} />
          <ContactInterestsList interests={data.interests} />
          <PendingNextActionRow value={data.pendingNextAction} />
          <LastObjectionRow value={data.lastObjection} />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-sections
// ---------------------------------------------------------------------------

function LinkedIdentities({
  identities,
}: {
  identities: CrossChannelContext["identities"];
}) {
  return (
    <Section title="Linked identities" countLabel={identities.length}>
      {identities.length === 0 ? (
        <Empty />
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="py-1 pr-2 font-medium">Channel</th>
              <th className="py-1 pr-2 font-medium">Display name</th>
              <th className="py-1 pr-2 font-medium">Phone</th>
              <th className="py-1 pr-2 font-medium">Linked</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {identities.map((i) => (
              <tr key={`${i.channel}:${i.externalId}`}>
                <td className="py-1.5 pr-2">
                  <ChannelBadge channel={i.channel} />
                </td>
                <td className="py-1.5 pr-2 text-slate-700">
                  {i.displayName ?? "-"}
                </td>
                <td className="py-1.5 pr-2 font-mono text-slate-700">
                  {i.phone ?? "-"}
                </td>
                <td className="py-1.5 pr-2 text-slate-500">
                  {formatRelative(i.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function RelatedConversations({
  related,
}: {
  related: CrossChannelContext["relatedConversations"];
}) {
  return (
    <Section title="Related conversations" countLabel={related.length}>
      {related.length === 0 ? (
        <Empty />
      ) : (
        <ul className="space-y-1.5">
          {related.map((r) => (
            <li key={r.id} className="text-xs">
              <div className="flex items-center gap-2">
                <ChannelBadge channel={r.channel} />
                {r.sourceChannel && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                    {r.sourceChannel}
                  </span>
                )}
                <span className="ml-auto text-slate-500">
                  {formatRelative(r.lastMessageAt)}
                </span>
              </div>
              {r.summary?.summaryText && (
                <p className="mt-1 line-clamp-2 text-slate-600">
                  {r.summary.summaryText}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function LastKnownProperty({
  value,
}: {
  value: CrossChannelContext["lastKnownProperty"];
}) {
  return (
    <Section title="Last known property">
      {!value || (!value.id && !value.name) ? (
        <p className="text-xs text-slate-400">No known property yet.</p>
      ) : (
        <div className="text-sm">
          <div className="font-medium text-slate-900">
            {value.name ?? "(unnamed)"}
          </div>
          <div className="font-mono text-[11px] text-slate-500">
            {value.id ?? "-"}
          </div>
        </div>
      )}
    </Section>
  );
}

function ContactInterestsList({
  interests,
}: {
  interests: CrossChannelContext["interests"];
}) {
  return (
    <Section title="Top interests" countLabel={interests.length}>
      {interests.length === 0 ? (
        <Empty />
      ) : (
        <ul className="space-y-1.5">
          {interests.map((i) => (
            <li key={i.id} className="flex items-center gap-2 text-xs">
              <span className="font-medium text-slate-900">
                {i.propertyName ?? "(unnamed)"}
              </span>
              <span className="font-mono text-[10px] text-slate-400">
                {i.propertyId ?? "-"}
              </span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                {i.signal}
              </span>
              <span className="text-slate-500">conf={i.confidence}</span>
              <span className="ml-auto text-slate-400">
                {formatRelative(i.lastSignalAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PendingNextActionRow({
  value,
}: {
  value: CrossChannelContext["pendingNextAction"];
}) {
  return (
    <Section title="Pending next action">
      {!value ? (
        <p className="text-xs text-slate-400">No pending action.</p>
      ) : (
        <div className="text-sm">
          <div className="flex items-center gap-2">
            <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
              {value.action}
            </span>
            <span className="text-xs text-slate-500">
              source: {value.source}
            </span>
            <span className="ml-auto text-xs text-slate-400">
              created {formatRelative(value.createdAt)}
            </span>
          </div>
          {value.targetPropertyId && (
            <div className="mt-1 font-mono text-[11px] text-slate-500">
              target property: {value.targetPropertyId}
            </div>
          )}
          {value.dueAt && (
            <div className="mt-1 text-[11px] text-slate-500">
              due {formatAbsolute(value.dueAt)}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function LastObjectionRow({ value }: { value: string | null }) {
  return (
    <Section title="Last objection">
      {!value ? (
        <p className="text-xs text-slate-400">None recorded.</p>
      ) : (
        <p className="text-sm text-slate-700">{value}</p>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function Section({
  title,
  countLabel,
  children,
}: {
  title: string;
  countLabel?: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-1.5 flex items-baseline gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          {title}
        </h3>
        {countLabel !== undefined && (
          <span className="text-[11px] text-slate-400">({countLabel})</span>
        )}
      </div>
      {children}
    </section>
  );
}

function Empty() {
  return <p className="text-xs text-slate-400">-</p>;
}
